/**
 * Lazy bootstrap for mysa-slider (#661). Keeps the full slider module off the
 * critical path: the HTML scroll-snap scroller, slide-1 media, SSR arrows and
 * SSR anchor dots all work before the module loads. This boot:
 *
 *   1. imports `@theme/mysa-slider` on idle / first interaction / near-viewport,
 *   2. makes SSR prev/next + dots operable before the module upgrades the custom
 *      element (native scrollBy / scrollIntoView), handing off once :defined,
 *   3. lazy-loads later-slide hero video bytes via IntersectionObserver so only
 *      the current/next slide's video downloads (slide 2+ stays data-src at t=0).
 *
 * No static dependency on `@theme/mysa-slider` — this file stays tiny and always
 * loads.
 */
import { requestIdleCallback } from "@theme/utilities";

const MODULE = "@theme/mysa-slider";
const MOBILE_QUERY = "(max-width: 479px)";

/** @type {WeakMap<HTMLElement, number>} */
let slotWidthCache = new WeakMap();

window.addEventListener(
  "resize",
  () => {
    slotWidthCache = new WeakMap();
  },
  { passive: true },
);

// --- Deferred import of the full module -----------------------------------

let moduleRequested = false;

const loadModule = () => {
  if (moduleRequested) return;
  moduleRequested = true;
  import(MODULE).catch(() => {
    // Allow a later trigger to retry if the network hiccups.
    moduleRequested = false;
  });
};

// Trigger 1: browser idle time.
requestIdleCallback(loadModule);

// Trigger 2: first pointer / keyboard / focus interaction inside any slider.
const onFirstInteraction = (event) => {
  const target = event.target;
  if (target instanceof Element && target.closest("mysa-slider")) {
    loadModule();
  }
};
for (const type of ["pointerdown", "keydown", "focusin"]) {
  document.addEventListener(type, onFirstInteraction, {
    capture: true,
    passive: true,
  });
}

// Trigger 3: a slider approaches the viewport. Covers the default variant's
// FOUC gate — `.slider--variant-default .slider-inner` sits at opacity 0 until
// `mysa-slider:defined`, so it must upgrade before the user reaches it.
if ("IntersectionObserver" in window) {
  const nearObserver = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          loadModule();
          observer.disconnect();
          return;
        }
      }
    },
    { rootMargin: "200px" },
  );
  for (const slider of document.querySelectorAll("mysa-slider")) {
    nearObserver.observe(slider);
  }
}

// --- Pre-upgrade nav operability -----------------------------------------
//
// Until the custom element is :defined, drive prev/next, dots and Arrow keys
// with native scrolling and keep the SSR dot state (active + aria-current) in
// sync, all by position — the stable reference across the content_for boundary,
// since block ids are documented as "subject to change". Once the component
// upgrades it owns nav via its delegated on:click / keydown handlers, and it
// adopts the current scroll position (no reset), so the handoff is seamless.

/** @param {Element} slider */
const realSlides = (slider) =>
  slider.querySelectorAll(".slider-item:not([data-clone])");

/** @param {Element} slider */
const dotsOf = (slider) => slider.querySelectorAll(".slider-nav-dot");

// Mirrors MysaSlider #updateDots' active-state contract (.active + aria-current).
// Keep the two in sync — a change to how the active dot is represented (e.g.
// adding aria-selected) must update both sides of the pre-/post-upgrade seam.
/** @param {Element} slider @param {number} index */
const setActiveDot = (slider, index) => {
  dotsOf(slider).forEach((dot, i) => {
    const active = i === index;
    dot.classList.toggle("active", active);
    active
      ? dot.setAttribute("aria-current", "true")
      : dot.removeAttribute("aria-current");
  });
};

/** Nearest slide index from the live scrollLeft, clamped to real slides.
 * @param {Element} slider */
const scrollIndex = (slider) => {
  const inner = slider.querySelector('[ref="inner"]');
  if (!(inner instanceof HTMLElement)) return 0;
  let slot = slotWidthCache.get(inner);
  if (!slot) {
    const first = inner.querySelector(".slider-item:not([data-clone])");
    slot =
      first instanceof HTMLElement && first.offsetWidth > 0
        ? first.offsetWidth
        : inner.clientWidth || 1;
    if (slot > 1) slotWidthCache.set(inner, slot);
  }
  const raw = Math.round(inner.scrollLeft / slot);
  const lastIndex = Math.max(0, realSlides(slider).length - 1);
  return Math.max(0, Math.min(raw, lastIndex));
};

/** @param {Element} slider */
const currentIndex = (slider) => {
  const fromDot = Array.from(dotsOf(slider)).findIndex((dot) =>
    dot.classList.contains("active"),
  );
  return fromDot >= 0 ? fromDot : scrollIndex(slider);
};

/** @param {Element} slider @param {number} index */
const goToIndex = (slider, index) => {
  const slides = realSlides(slider);
  if (slides.length === 0) return;
  const clamped = Math.max(0, Math.min(index, slides.length - 1));
  const slide = slides[clamped];
  if (slide instanceof HTMLElement) {
    // Instant (not smooth): .slider-inner sets `scroll-behavior: smooth` in CSS,
    // so a smooth hop would leave scrollLeft mid-animation. If the module upgrades
    // during that window it can't tell which slide the buyer meant. `instant`
    // overrides the CSS so scrollLeft is authoritative the moment we return, and
    // the module simply adopts the settled position on handoff (#661).
    slide.scrollIntoView({
      inline: "start",
      block: "nearest",
      behavior: "instant",
    });
  }
  setActiveDot(slider, clamped);
};

/** @param {Element} target */
const undefinedSlider = (target) => {
  const slider = target.closest("mysa-slider");
  return slider && !slider.matches(":defined") ? slider : null;
};

document.addEventListener(
  "click",
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const slider = undefinedSlider(target);
    if (!slider) return;

    const dot = target.closest(".slider-nav-dot");
    if (dot instanceof HTMLElement) {
      event.preventDefault();
      goToIndex(slider, Number(dot.dataset.slideIndex ?? ""));
      return;
    }
    if (target.closest(".slider-nav-button--prev")) {
      event.preventDefault();
      goToIndex(slider, currentIndex(slider) - 1);
    } else if (target.closest(".slider-nav-button--next")) {
      event.preventDefault();
      goToIndex(slider, currentIndex(slider) + 1);
    }
  },
  { capture: true },
);

document.addEventListener(
  "keydown",
  (event) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const slider = undefinedSlider(target);
    if (!slider) return;
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 1 : -1;
    goToIndex(slider, currentIndex(slider) + delta);
  },
  { capture: true },
);

// Keep the SSR active dot in sync with native scroll (touch swipe / wheel /
// trackpad) before upgrade, so it stays the single source of truth the module
// adopts in #setInitialScroll — not just arrow/dot nav. `scroll` doesn't bubble
// but capture-phase reaches the inner scroller; no-op once every slider is
// :defined (the module then owns dot state). Cheap: bails fast off-slider.
document.addEventListener(
  "scroll",
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const slider = undefinedSlider(target);
    if (!slider) return;
    setActiveDot(slider, scrollIndex(slider));
  },
  { capture: true, passive: true },
);

// --- Lazy later-slide video bytes ----------------------------------------
//
// Only the slide actually in the viewport loads + plays its video (#661).
// Adjacent slides sit flush at the viewport edges in the scroll-snap track, so
// any preload lead would fetch slide 2 at rest — instead its data-src stays put
// until it scrolls in, and the LCP poster covers the brief load. Result: no
// second-slide video request at t=0.

// Single MediaQueryList reused for every breakpoint check + the flip listener.
const mobileMql = matchMedia(MOBILE_QUERY);

// A slide can carry a `--desktop`/`--mobile` video pair in one background, or
// separate `.hero-slide__background--desktop|--mobile` wrappers, so detect the
// breakpoint from the video class or its wrapper. Untagged single videos apply
// on any breakpoint.
/** @param {HTMLVideoElement} video */
const wantsThisBreakpoint = (video) => {
  const isMobile = mobileMql.matches;
  const mobileVid =
    video.classList.contains("hero-slide__background-video--mobile") ||
    Boolean(video.closest(".hero-slide__background--mobile"));
  const desktopVid =
    video.classList.contains("hero-slide__background-video--desktop") ||
    Boolean(video.closest(".hero-slide__background--desktop"));
  return isMobile ? !desktopVid : !mobileVid;
};

// Shopify CDN names transcodes `{HD|SD}-{height}p-{bitrate}Mbps`. Liquid often
// lists only the top MP4. `data-mp4-rung` inserts a 480p sibling before that
// file. Exactly one source gets `src`: a Range GET (1 byte) picks the first
// reachable URL. Do not listen for `error` to promote the next file — PSI's
// lab Chrome fetches 480p, fails H.264 decode, and that event looks like a 404
// (~3 MB + ~17 MB). Generation invalidates an in-flight probe on rotate or
// when the slide leaves the viewport.
const MP4_RUNG_RE = /(?:HD|SD)-\d+p-[\d.]+Mbps/;
const MOBILE_MP4_RUNG = "SD-480p-1.5Mbps";

/** @type {WeakMap<HTMLVideoElement, number>} */
const playGeneration = new WeakMap();

/** @param {HTMLVideoElement} video */
const bumpPlayGeneration = (video) => {
  const next = (playGeneration.get(video) ?? 0) + 1;
  playGeneration.set(video, next);
  return next;
};

/** @param {string} url @param {string} rung */
const rewriteMp4Rung = (url, rung) => {
  if (!url || !rung || !MP4_RUNG_RE.test(url)) return url;
  return url.replace(MP4_RUNG_RE, rung);
};

/** @param {HTMLVideoElement} video */
const expandMobileMp4Sources = (video) => {
  const flagged = [...video.querySelectorAll("source[data-mp4-rung]")];
  for (const source of flagged) {
    const listed = source.dataset.src ?? "";
    if (!listed || /SD-480p-/i.test(listed)) continue;
    const capped = rewriteMp4Rung(listed, MOBILE_MP4_RUNG);
    if (!capped || capped === listed) continue;
    const prev = source.previousElementSibling;
    if (
      prev instanceof HTMLSourceElement &&
      prev.getAttribute("data-src") === capped
    ) {
      continue;
    }
    const probe = source.cloneNode(false);
    probe.removeAttribute("data-mp4-rung");
    probe.setAttribute("data-src", capped);
    source.before(probe);
  }
};

/** @param {HTMLVideoElement} video */
const tryPlay = (video) => {
  const played = video.play();
  if (played && typeof played.catch === "function") played.catch(() => {});
};

/** @param {string} url */
const rungExists = async (url) => {
  if (!url) return false;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Range: "bytes=0-0" },
      mode: "cors",
    });
    return response.ok;
  } catch {
    // CORS / network: `<video>` can still fetch this URL. A 404 is a normal
    // response and does not throw — only that case should skip to the next rung.
    return true;
  }
};

/**
 * @param {HTMLVideoElement} video
 * @param {number} generation
 * @param {HTMLSourceElement[]} sources
 */
const promoteReachableSource = async (video, generation, sources) => {
  let chosen = null;
  for (const source of sources) {
    if (playGeneration.get(video) !== generation) return;
    if (await rungExists(source.dataset.src ?? "")) {
      chosen = source;
      break;
    }
  }
  if (playGeneration.get(video) !== generation) return;
  if (!wantsThisBreakpoint(video)) return;
  if (!(chosen instanceof HTMLSourceElement)) return;
  chosen.setAttribute("src", chosen.dataset.src ?? "");
  video.load();
  tryPlay(video);
};

/** @param {HTMLVideoElement} video */
const playVideo = (video) => {
  expandMobileMp4Sources(video);
  const generation = bumpPlayGeneration(video);
  const loaded = video.querySelector("source[data-src][src]");
  if (loaded instanceof HTMLSourceElement) {
    tryPlay(video);
    return;
  }
  const sources = [...video.querySelectorAll("source[data-src]")];
  if (sources.length === 0) {
    tryPlay(video);
    return;
  }
  void promoteReachableSource(video, generation, sources);
};

/** @param {HTMLVideoElement} video */
const pauseVideo = (video) => {
  if (!video.paused) video.pause();
};

// Pause and shed bytes: strip loaded `src` back to `data-src` so a breakpoint
// flip doesn't leave the now-unwanted variant downloaded alongside the wanted
// one. Reverses playVideo; the source reloads if this breakpoint returns.
/** @param {HTMLVideoElement} video */
const resetVideo = (video) => {
  bumpPlayGeneration(video);
  pauseVideo(video);
  let removed = false;
  const sources = /** @type {NodeListOf<HTMLSourceElement>} */ (
    video.querySelectorAll("source[data-src]")
  );
  for (const source of sources) {
    if (source.getAttribute("src")) {
      source.removeAttribute("src");
      removed = true;
    }
  }
  if (removed) video.load();
};

// Load + play the breakpoint-appropriate video in a slide; shed the other.
/** @param {Element} slide */
const playSlideVideo = (slide) => {
  for (const video of slide.querySelectorAll("video")) {
    wantsThisBreakpoint(video) ? playVideo(video) : resetVideo(video);
  }
};

/** @param {Element} slide */
const pauseSlideVideo = (slide) => {
  for (const video of slide.querySelectorAll("video")) {
    bumpPlayGeneration(video);
    pauseVideo(video);
  }
};

// Idempotent video wiring. Built lazily so a page with no slider videos pays
// nothing, and re-runnable so section re-renders (theme editor
// `shopify:section:load`, client-side Section Rendering) re-observe their fresh
// `<video data-src>` nodes — the old lifecycle-driven activation lived in the
// custom element, so this boot must own re-scanning too (#661).
const observedSlides = new WeakSet();
const visibleSlides = new Set();
/** @type {IntersectionObserver | null} */
let videoObserver = null;

const ensureVideoObserver = () => {
  if (videoObserver) return videoObserver;
  videoObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          visibleSlides.add(entry.target);
          playSlideVideo(entry.target);
        } else {
          visibleSlides.delete(entry.target);
          pauseSlideVideo(entry.target);
        }
      }
    },
    // Majority in view before loading — keeps the flush-adjacent next slide from
    // fetching at rest.
    { rootMargin: "0px", threshold: 0.5 },
  );
  // A breakpoint flip must re-pick desktop vs mobile source for in-view slides.
  mobileMql.addEventListener("change", () => {
    for (const slide of visibleSlides) playSlideVideo(slide);
  });
  return videoObserver;
};

/** @param {ParentNode} [root] */
const observeSliderVideos = (root = document) => {
  // `root` can be the document, a reloaded section subtree, or a single
  // <mysa-slider> (from the slides-changed event) — which querySelectorAll would
  // exclude itself from, so fold it in explicitly.
  const sliders =
    root instanceof Element && root.matches("mysa-slider")
      ? [root]
      : Array.from(root.querySelectorAll("mysa-slider"));

  const slides = [];
  for (const slider of sliders) {
    // Exclude loop clones (default variant): they're cloneNode(true) copies that
    // carry the same lazy video markup, so observing them would double-load the
    // real slide's bytes. The real (non-clone) slide is the source of truth;
    // centered/video sliders have no clones anyway.
    for (const slide of slider.querySelectorAll(
      ".slider-item:not([data-clone])",
    )) {
      if (slide.querySelector("video") && !observedSlides.has(slide)) {
        slides.push(slide);
      }
    }
  }
  if (slides.length === 0) return;

  if ("IntersectionObserver" in window) {
    const observer = ensureVideoObserver();
    for (const slide of slides) {
      observedSlides.add(slide);
      observer.observe(slide);
    }
  } else {
    // No IntersectionObserver: play the first newly-found slide's video only.
    observedSlides.add(slides[0]);
    playSlideVideo(slides[0]);
  }
};

observeSliderVideos();

// Theme editor / dynamic Section Rendering swaps in fresh section HTML with new
// `<video data-src>` nodes; re-scan the reloaded subtree so their bytes still
// load on scroll-in.
document.addEventListener("shopify:section:load", (event) => {
  const target = event.target;
  observeSliderVideos(target instanceof Element ? target : document);
});

// The upgraded module morphs its own slides (variant:update / inner
// MutationObserver reinit) and announces it via `mysa-slider:slides-changed`;
// re-scan that slider so post-morph `<video data-src>` nodes are observed too.
document.addEventListener("mysa-slider:slides-changed", (event) => {
  const target = event.target;
  observeSliderVideos(target instanceof Element ? target : document);
});
